<template>
  <div class="auth-wrapper auth-v1 px-2">
    <div class="auth-inner py-2">
      <b-card class="mb-0 auth-card">
        <b-link class="brand-logo">
          <Logo />
          <h2 class="brand-text text-primary ml-1">IMEI Checker</h2>
        </b-link>

        <div class="demo-spacing-0 mb-2">
          <b-alert variant="warning" dismissible show class="mb-0">
            <div class="alert-body">
              <span>
                <center>
                  We don't do unlocks, unlock iCloud or track stolen phones.
                  <br /><br />
                  For any other information,
                  <a href="https://wa.me/3530852108011?text=Hi, I need help "
                    >Contact us by WhatsApp</a
                  >
                  <br />
                </center>
              </span>
            </div>
          </b-alert>
        </div>

        <div class="auth-header">
          <h2 class="mb-0">Login</h2>
          <div class="auth-subtitle text-muted">
            Sign in with your password or a magic link.
          </div>
        </div>

        <!-- Password login -->
        <validation-observer ref="loginForm" #default="{ invalid }">
          <b-form
            class="auth-login-form mt-2"
            @submit.prevent="$refs.recaptcha.execute()"
          >
            <b-form-group label-for="emailOrUsername" label="Email or Username">
              <validation-provider
                #default="{ errors }"
                name="Email or Username"
                rules="required"
              >
                <b-form-input
                  id="emailOrUsername"
                  v-model="form.userkey"
                  name="login-email"
                  :state="errors.length > 0 ? false : null"
                  placeholder="john@example.com"
                  autofocus
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <div class="d-flex justify-content-between">
              <label for="password">Password</label>
              <b-link :to="{ name: 'forget-pass' }">
                <small>Forgot Password?</small>
              </b-link>
            </div>

            <Password rules="required" v-model="form.password" label="" />

            <div class="text-center">
              <Alert v-model="error" />
              <ReCaptcha ref="recaptcha" :submit="submit" />

              <b-button
                v-if="!loading"
                variant="primary"
                type="submit"
                block
                :disabled="invalid"
              >
                Sign in
              </b-button>
              <b-spinner v-else variant="primary" type="grow" label="Loading" />
            </div>
          </b-form>
        </validation-observer>

        <!-- Magic link -->
        <div class="divider my-2">
          <div class="divider-text">or</div>
        </div>

        <!-- Wrapped CTA (button + hint) -->
        <div class="magic-box mb-1" :class="{ 'is-open': showMagicLink }">
          <b-button
            variant="outline-primary"
            block
            class="magic-btn"
            @click="showMagicLink = !showMagicLink"
          >
            Sign in with a magic link
          </b-button>

          <div class="magic-hint text-muted">
            We email you a one-time sign-in link. No password needed.
          </div>
        </div>

        <b-collapse v-model="showMagicLink">
          <div class="magic-panel mb-1">
            <div class="magic-panel-head">
              <div class="magic-panel-title">Send sign-in link</div>
              <div class="magic-panel-subtitle text-muted">
                Use the email on your account.
              </div>
            </div>

            <validation-observer ref="magicForm" #default="{ invalid: invalidMagic }">
              <b-form
                class="auth-login-form mb-0"
                @submit.prevent="$refs.magicRecaptcha.execute()"
              >
                <b-form-group label-for="magicEmail" label="Email" class="mb-1">
                  <validation-provider
                    #default="{ errors }"
                    name="Email"
                    rules="required|email"
                  >
                    <b-form-input
                      id="magicEmail"
                      v-model="magic.email"
                      type="email"
                      name="magic-email"
                      :state="errors.length > 0 ? false : null"
                      placeholder="john@example.com"
                      class="magic-input"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>

                <Alert v-model="magicError" />

                <b-alert
                  variant="success"
                  :show="magicSuccess"
                  dismissible
                  class="mb-1"
                  @dismissed="magicSuccess = false"
                >
                  <div class="alert-body">
                    If an account exists for this email, we sent a sign-in link. Check
                    your inbox (and spam).
                  </div>
                </b-alert>

                <ReCaptcha ref="magicRecaptcha" :submit="submitMagicLink" />

                <b-button
                  v-if="!magicLoading"
                  variant="primary"
                  type="submit"
                  block
                  :disabled="invalidMagic"
                >
                  Email me the link
                </b-button>
                <b-spinner v-else variant="primary" type="grow" label="Loading" />
              </b-form>
            </validation-observer>
          </div>
        </b-collapse>

        <b-card-text class="text-center mt-2">
          <span>New here? </span>
          <b-link :to="{ name: 'register' }">
            <span>Create an account</span>
          </b-link>
        </b-card-text>

        <div class="divider my-2">
          <div class="divider-text">or sign in with</div>
        </div>

        <div class="auth-footer-btn d-flex justify-content-center">
          <b-button @click="loginWith('google')" variant="google">
            <div class="d-flex align-items-center">
              <div class="ico google-icon mr-1" />
              <span>Google</span>
            </div>
          </b-button>
        </div>

        <b-card-text class="text-center text-muted small mt-2 mb-0">
          This site is protected by reCAPTCHA and the Google
          <b-link
            href="https://policies.google.com/privacy"
            target="_blank"
            rel="noopener"
            >Privacy Policy</b-link
          >
          and
          <b-link
            href="https://policies.google.com/terms"
            target="_blank"
            rel="noopener"
            >Terms of Service</b-link
          >
          apply.
        </b-card-text>
      </b-card>
    </div>
  </div>
</template>

<script>
import Auth from "@/services/auth.service.js";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import {
  BAlert,
  BCollapse,
  BButton,
  BForm,
  BFormInput,
  BFormGroup,
  BCard,
  BLink,
  BCardText,
  BSpinner,
} from "bootstrap-vue";
import Logo from "@core/layouts/components/Logo.vue";
import { required, email } from "@validations";
import Password from "@/components/util/Password.vue";
import Alert from "@/components/util/Alert.vue";
import ReCaptcha from "@/components/util/ReCaptcha.vue";

export default {
  components: {
    BAlert,
    BCollapse,
    BButton,
    BForm,
    BFormInput,
    BFormGroup,
    BCard,
    BLink,
    BCardText,
    BSpinner,
    Logo,
    ValidationProvider,
    ValidationObserver,
    Password,
    Alert,
    ReCaptcha,
  },
  data: () => ({
    loading: false,
    form: {
      userkey: "",
      password: "",
    },

    showMagicLink: false,
    magic: {
      email: "",
    },
    magicLoading: false,
    magicError: null,
    magicSuccess: false,

    error: null,

    required,
    email,
  }),
  mounted() {
    if (this.$route?.query?.magic) this.showMagicLink = true;
  },
  methods: {
    loginWith(provider) {
      Auth.loginWith(provider);
    },

    async submit(token) {
      this.loading = true;
      const success = await this.$refs.loginForm.validate();
      if (success) {
        try {
          await Auth.login({ ...this.form, recaptcha: token });
        } catch (e) {
          const status = e.response?.status;
          if (status === 403) {
            this.error = e.response.data;
          } else if (status === 422) {
            this.error = Object.values(e.response.data.errors)[0][0];
          } else {
            this.error = "Server error. Please check your connection and try again.";
          }
        }
      }
      this.loading = false;
    },

    async submitMagicLink(token) {
      this.magicLoading = true;
      this.magicError = null;
      this.magicSuccess = false;

      const success = await this.$refs.magicForm.validate();
      if (success) {
        try {
          await Auth.requestMagicLink({
            email: this.magic.email,
            recaptcha: token,
          });

          // Always generic success (prevents account enumeration)
          this.magicSuccess = true;
        } catch (e) {
          const status = e.response?.status;
          if (status === 422) {
            this.magicError = Object.values(e.response.data.errors)[0][0];
          } else {
            this.magicError =
              e.response?.data?.message ||
              e.response?.data ||
              "Server error. Please check your connection and try again.";
          }
        }
      }

      this.magicLoading = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@core/scss/vue/pages/page-auth.scss";

.auth-card {
  border-radius: 0.9rem;
}

.auth-header {
  margin-top: 0.25rem;
}
.auth-subtitle {
  margin-top: 0.25rem;
  font-size: 0.85rem;
}

.ico {
  width: 15px;
  height: 15px;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}
.google-icon {
  background-image: url("~@/assets/images/pages/login/google.svg");
}
.apple-icon {
  background-image: url("~@/assets/images/pages/login/apple.svg");
}
.facebook-icon {
  background-image: url("~@/assets/images/pages/login/facebook.svg");
}

/* Magic link CTA box */
.magic-box {
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: rgba(0, 0, 0, 0.03);
  border-radius: 0.85rem;
  padding: 0.75rem;
  transition: all 0.15s ease;
}
.magic-box.is-open {
  border-color: rgba(115, 103, 240, 0.35);
  background: rgba(115, 103, 240, 0.06);
}
.magic-btn {
  border-radius: 0.65rem;
}
.magic-hint {
  margin-top: 0.45rem;
  font-size: 0.78rem;
  line-height: 1.15;
  text-align: center;
}

/* Magic form panel (inside collapse) */
.magic-panel {
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: rgba(0, 0, 0, 0.02);
  border-radius: 0.85rem;
  padding: 0.85rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}
.magic-panel-head {
  margin-bottom: 0.65rem;
  text-align: center;
}
.magic-panel-title {
  font-weight: 600;
  font-size: 0.95rem;
  line-height: 1.1;
}
.magic-panel-subtitle {
  margin-top: 0.25rem;
  font-size: 0.78rem;
}

/* Slightly nicer input shape in magic panel */
.magic-input {
  border-radius: 0.65rem;
}

/* Dark layout overrides (theme-aware) */
.dark-layout {
  .magic-box {
    border-color: rgba(255, 255, 255, 0.10);
    background: rgba(255, 255, 255, 0.04);
  }

  .magic-box.is-open {
    border-color: rgba(115, 103, 240, 0.55);
    background: rgba(115, 103, 240, 0.14);
  }

  .magic-panel {
    border-color: rgba(255, 255, 255, 0.10);
    background: rgba(255, 255, 255, 0.03);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
  }
}
</style>
